// src/modules/Permissions/PermissionsPage.js - WITH DUAL SYSTEM SUPPORT & I18N

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import styles from '../Users/UsersPage.module.css';
import permissionsStyles from './PermissionsPage.module.css';

// Common Components
import {
    Toast,
    SearchBar,
    EmptyState
    // TableSkeleton removed - was imported but never used
} from '../../components/common';

const ERP_ROLES = [
    { value: 'Administracao', label: 'Administração' },
    { value: 'Comercial', label: 'Comercial' },
    { value: 'Diretoria', label: 'Diretoria (Admin)' },
    { value: 'Laboratorio', label: 'Laboratório' },
    { value: 'Logistica', label: 'Logística' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'TI', label: 'TI' },
    { value: 'Inspetor', label: 'Inspetor' },
    { value: 'Operacional', label: 'Operacional' },
    { value: 'Visitante', label: 'Visitante' }
];

const SYSTEM_TYPES = {
    BASE: 'base',
    LUXURY: 'luxury'
};

// ============================================
// TRANSLATION KEYS FOR MENU ITEMS
// ============================================
const getTranslationKey = (text) => {
    const keyMap = {
        'Dashboard': 'navigation.dashboard',
        'Parâmetros': 'navigation.parameters',
        'Cadastros': 'navigation.registrations',
        'Projeto': 'navigation.projects',
        'Pedidos': 'navigation.orders',
        'Produção': 'navigation.production',
        'Financeiro': 'navigation.financial',
        'Fiscal': 'navigation.fiscal',
        'Controle de Qualidade': 'navigation.quality',
        'Estoque': 'navigation.inventory',
        'Relatórios': 'navigation.reports',
        'AjudaAí!': 'navigation.helpdesk',
        'Usuários': 'navigation.users',
        'Permissões': 'navigation.permissions',
        'Checklist': 'navigation.checklist',
        'Relatório': 'navigation.reports',
        'Modelos': 'navigation.models',
        'Cargos': 'navigation.positions',
        'Prefixos': 'navigation.prefixes',
        'Inventário': 'navigation.inventory',
        'Região': 'navigation.region',
        'Unidade de Medida': 'navigation.unitOfMeasure',
        'Situação de Cheque': 'navigation.checkStatus',
        'Público Alvo': 'navigation.targetAudience',
        'Modelos de Projeto': 'navigation.projectModels',
        'Cores': 'navigation.colors',
        'Critério de Qualidade': 'navigation.qualityCriteria',
        'Parâmetros de Preparo': 'navigation.preparationSetup',
        'Embalagem': 'navigation.packaging',
        'Motivo de Contato (SAC)': 'navigation.contactReason',
        'Situação (SAC)': 'navigation.situation',
        'Motivo Devolução Produto': 'navigation.productReturn',
        'Motivo Alteração Estoque': 'navigation.stockChange',
        'Categoria de Produtos': 'navigation.productCategory',
        'Sub-Categoria': 'navigation.subCategory',
        'Bancos': 'navigation.banks',
        'Condição de Faturamento': 'navigation.billingCondition',
        'Tipos': 'navigation.types',
        'Tipo de Cliente': 'navigation.clientType',
        'Tipo de Caixa': 'navigation.boxType',
        'Tipo de Produto': 'navigation.productType',
        'Tipo de Endereço': 'navigation.addressType',
        'Tipo de Telefone': 'navigation.phoneType',
        'Tipo de Contato': 'navigation.contactType',
        'Tipo Resultado Contato': 'navigation.contactResultType',
        'Tipo Doc. Pagamento': 'navigation.paymentDocType',
        'Tipo de Projeto': 'navigation.projectType',
        'Tipo de Função': 'navigation.positionType',
        'Tipo de Natureza': 'navigation.natureType',
        'Tipo de Empresa': 'navigation.companyType',
        'Matéria-Prima': 'navigation.rawMaterials',
        'Produtos': 'navigation.products',
        'Fornecedores': 'navigation.suppliers',
        'Procedimentos': 'navigation.procedures',
        'Clientes': 'navigation.clients',
        'Executivos de Conta': 'navigation.accountExecutives',
        'Transportadoras': 'navigation.carriers',
        'Desenvolvimento': 'navigation.development',
        'Acompanhamento de Projeto': 'navigation.projectTracking',
        'Pedido de Venda': 'navigation.salesOrder',
        'Requisição de Compra': 'navigation.purchaseRequest',
        'Pedido de Compra': 'navigation.purchaseOrder',
        'Aprovação de Pedido de Venda': 'navigation.salesOrderApproval',
        'Aprovação de Pedido de Compra': 'navigation.purchaseOrderApproval',
        'Entrada do Pedido de Compra': 'navigation.purchaseOrderEntry',
        'Emissão de Etiqueta de Código de Barras': 'navigation.barcodeEmission',
        'Ordens de Produção': 'navigation.productionOrders',
        'Fases de Produção': 'navigation.productionPhases',
        'Forecast': 'navigation.forecast',
        'Centro de Custo': 'navigation.costCenter',
        'Plano de Contas': 'navigation.accountPlan',
        'Manifesto do Destinatário': 'navigation.recipientManifest',
        'Cadastro de NCM': 'navigation.ncmRegister',
        'Cadastro de CFOP': 'navigation.cfopRegister',
        'Análise de Embalagens': 'navigation.packagingAnalysis',
        'Localizações': 'navigation.locations',
        'Entrada de Estoque': 'navigation.stockEntry',
        'Saída de Estoque': 'navigation.stockOutflow',
        'Consulta de Estoque': 'navigation.stockQuery',
        'Estoque Atual': 'navigation.currentStock',
        'Listagem de Amostras': 'navigation.sampleList',
        'Relatório de Usuários': 'navigation.userReport',
        'Listagem de Clientes': 'navigation.clientList',
        'Listagem de Fornecedores': 'navigation.supplierList',
        'Listagem de Transportadoras': 'navigation.carrierList',
        'Listagem de Produtos': 'navigation.productList',
        'Ficha de Produto': 'navigation.productRecord',
        'Relatório de Suporte': 'navigation.supportReport',
        'Materiais': 'navigation.materials',
        'Central de Suporte': 'navigation.supportCenter',
        'Gerenciamento de Tickets': 'navigation.ticketManagement',
        'Checklists': 'navigation.checklists',
        'Balanço de Moeda': 'navigation.balance'
    };
    return keyMap[text] || text;
};

// ============================================
// IMPORTANT: Generate ID from ORIGINAL Portuguese text
// ============================================
const generateIdFromText = (text) => {
    return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, '_')
        .replace(/[^\w]/g, '');
};

// Get item ID - ALWAYS uses the ORIGINAL Portuguese text
const getItemId = (item) => {
    if (item.id) return item.id;
    return generateIdFromText(item.text);
};

// Translate menu item text for display
const translateMenuItem = (item, t) => {
    if (!item) return item;

    const translated = { ...item };

    // Lock the id from the original text first, then translate for display.
    translated.id = item.id || generateIdFromText(item.text);
    const translationKey = getTranslationKey(item.text);
    translated.text = t(translationKey);

    if (item.subModules) {
        translated.subModules = item.subModules.map(subItem => {
            if (subItem.type === 'sub-group') {
                return {
                    ...subItem,
                    id: subItem.id || generateIdFromText(subItem.text),
                    text: t(getTranslationKey(subItem.text)),
                    subModules: subItem.subModules.map(link => ({
                        ...link,
                        id: link.id || generateIdFromText(link.text),
                        text: t(getTranslationKey(link.text))
                    }))
                };
            }
            return {
                ...subItem,
                id: subItem.id || generateIdFromText(subItem.text),
                text: t(getTranslationKey(subItem.text))
            };
        });
    }

    return translated;
};

// Action labels with translation keys
const ACTION_LABELS = {
    create: 'actions.create',
    edit: 'actions.edit',
    delete: 'actions.delete',
    view: 'actions.view',
    export: 'actions.export',
    import: 'actions.import',
    approve: 'actions.approve',
    print: 'actions.print',
    generate: 'actions.generate',
    track: 'actions.track',
    return: 'actions.return',
    verify: 'actions.verify',
    produce: 'actions.produce',
    pay: 'actions.pay',
    start: 'actions.start',
    cancel: 'actions.cancel',
    sync: 'actions.sync',
    manifest: 'actions.manifest',
    move: 'actions.move',
    duplicate: 'actions.duplicate',
    convert: 'actions.convert',
};

// ============================================
// BASE ERP MENU (E-MRP)
// ============================================
const menuItemsBase = [
    { id: 'dashboard', text: 'Dashboard', path: '/dashboard', type: 'link' },
    {
        id: 'parametros',
        text: 'Parâmetros', type: 'group',
        subModules: [
            { text: 'Bancos', path: '/parametros/bancos' },
            { text: 'Cargos', path: '/parametros/cargos', actions: ['create', 'edit', 'delete'] },
            { text: 'Categoria de Produtos', path: '/parametros/categoria-de-produto', actions: ['create', 'edit', 'delete'] },
            { text: 'Cores', path: '/parametros/cores', actions: ['create', 'edit', 'delete'] },
            { text: 'Condição de Faturamento', path: '/parametros/condicao-de-faturamento', actions: ['create', 'edit', 'delete'] },
            { text: 'Critério de Qualidade', path: '/parametros/criterio-de-qualidade', actions: ['create', 'edit', 'delete'] },
            { text: 'Embalagem', path: '/parametros/embalagem', actions: ['create', 'edit', 'delete'] },
            { text: 'Inventário', path: '/parametros/inventario' },
            { text: 'Modelos de Projeto', path: '/parametros/modelos-de-projeto', actions: ['create', 'edit', 'delete'] },
            { text: 'Motivo Alteração Estoque', path: '/parametros/motivo-de-alteracao-de-estoque', actions: ['create', 'edit', 'delete'] },
            { text: 'Motivo Devolução Produto', path: '/parametros/motivo-de-devolucao-produto', actions: ['create', 'edit', 'delete'] },
            { text: 'Motivo de Contato (SAC)', path: '/parametros/motivo-de-contato', actions: ['create', 'edit', 'delete'] },
            { text: 'Parâmetros de Preparo', path: '/parametros/setup-de-preparo', actions: ['create', 'edit', 'delete'] },
            { text: 'Prefixos', path: '/parametros/prefixos', actions: ['create', 'edit', 'delete'] },
            { text: 'Público Alvo', path: '/parametros/publico-alvo', actions: ['create', 'edit', 'delete'] },
            { text: 'Modelos', path: '/parametros/modelos', actions: ['create', 'edit', 'delete'] },
            { text: 'Região', path: '/parametros/regiao', actions: ['create', 'edit', 'delete'] },
            { text: 'Situação (SAC)', path: '/parametros/situacao', actions: ['create', 'edit', 'delete'] },
            { text: 'Situação de Cheque', path: '/parametros/situacao-de-cheque', actions: ['create', 'edit', 'delete'] },
            { text: 'Sub-Categoria', path: '/parametros/sub-categoria', actions: ['create', 'edit', 'delete'] },
            { text: 'Unidade de Medida', path: '/parametros/unidade-de-medida', actions: ['create', 'edit', 'delete'] },
            {
                id: 'tipos',
                text: 'Tipos', path: '/parametros/tipos', type: 'sub-group',
                subModules: [
                    { text: 'Tipo de Caixa', path: '/parametros/tipo/caixa', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Palete', path: '/parametros/tipo/palete', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Cliente', path: '/parametros/tipo/cliente', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Contato', path: '/parametros/tipo/contato', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Produto', path: '/parametros/tipo/produto', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Empresa', path: '/parametros/tipo/empresa', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Endereço', path: '/parametros/tipo/endereco', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Função', path: '/parametros/tipo/funcao', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Telefone', path: '/parametros/tipo/telefone', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Natureza', path: '/parametros/tipo/natureza', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo de Projeto', path: '/parametros/tipo/projeto', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo Doc. Pagamento', path: '/parametros/tipo/documento-pagamento', actions: ['create', 'edit', 'delete'] },
                    { text: 'Tipo Resultado Contato', path: '/parametros/tipo/resultado-contato', actions: ['create', 'edit', 'delete'] },
                ]
            },
        ]
    },
    {
        id: 'cadastros',
        text: 'Cadastros', type: 'group',
        subModules: [
            { text: 'Clientes', path: '/cadastros/clientes', actions: ['create', 'edit', 'delete'] },
            { text: 'Executivos de Conta', path: '/cadastros/executivos-de-conta', actions: ['create', 'edit', 'delete'] },
            { text: 'Fornecedores', path: '/cadastros/fornecedores', actions: ['create', 'edit', 'delete'] },
            { text: 'Procedimentos', path: '/cadastros/procedimentos', actions: ['create', 'edit', 'delete'] },
            { text: 'Produtos', path: '/cadastros/produtos', actions: ['create', 'edit', 'delete'] },
            { text: 'Matéria-Prima', path: '/cadastros/materia-prima', actions: ['create', 'edit', 'delete'] },
            { text: 'Transportadoras', path: '/cadastros/transportadora', actions: ['create', 'edit', 'delete'] },
        ]
    },
    {
        id: 'projeto',
        text: 'Projeto', type: 'group',
        subModules: [
            { text: 'Acompanhamento de Projeto', path: '/projeto/acompanhamento-de-projeto', actions: ['edit', 'generate'] },
            { text: 'Desenvolvimento', path: '/projeto/desenvolvimento', actions: ['create', 'edit', 'delete', 'generate'] },
            { text: 'Requisição de Amostra', path: '/projeto/requisicao-de-amostra', actions: ['create', 'edit', 'delete', 'generate'] },
        ]
    },
    {
        id: 'pedidos',
        text: 'Pedidos', type: 'group',
        subModules: [
            { text: 'Pedido de Venda', path: '/pedidos/pedido-de-venda', actions: ['create', 'edit', 'delete', 'view', 'approve', 'print', 'track'] },
            { text: 'Pedido de Compra', path: '/pedidos/pedido-de-compra', actions: ['create', 'view', 'track', 'edit', 'delete', 'pay'] },
            { text: 'Requisição de Compra', path: '/pedidos/requisicao-de-compra', actions: ['create', 'view', 'edit', 'delete', 'convert', 'approve'] },
            { text: 'Retorno de pedido para análise', path: '/pedidos/retorno-para-analise'},
            { text: 'Aprovação de Pedido de Venda', path: '/pedidos/aprovacao-de-pedido-de-venda', actions: ['view', 'approve', 'print', 'track', 'generate', 'return', 'verify', 'produce', 'pack'] },
            { text: 'Aprovação de Pedido de Compra', path: '/pedidos/aprovacao-de-pedido-de-compra', actions: ['view', 'approve', 'track'] },
            { text: 'Entrada do Pedido de Compra', path: '/pedidos/entrada-de-pedido-de-compra', actions: ['create', 'track', 'approve'] },
        ]
    },
    {
        id: 'producao',
        text: 'Produção', type: 'group',
        subModules: [
            { text: 'Emissão de Etiqueta de Código de Barras', path: '/producao/emissao-de-codigo-de-barras', actions: ['create', 'generate', 'delete'] },
            { text: 'Ordens de Produção', path: '/producao/ordens-de-producao', actions: ['create', 'export', 'delete', 'generate'] },
            { text: 'Fases de Produção', path: '/producao/fases-de-producao', actions: ['cancel', 'generate', 'start', 'view'] },
            { text: 'Forecast', path: '/producao/forecast' },
        ]
    },
    {
        id: 'financeiro',
        text: 'Financeiro', type: 'group',
        subModules: [
            { text: 'Centro de Custo', path: '/financeiro/centro-de-custo', actions: ['create', 'edit', 'delete'] },
            { text: 'Plano de Contas', path: '/financeiro/plano-de-contas', actions: ['create', 'edit', 'delete'] },
        ]
    },
    {
        id: 'fiscal',
        text: 'Fiscal', type: 'group',
        subModules: [
            { text: 'Manifesto do Destinatário', path: '/fiscal/manifesto-do-destinatario', actions: ['view', 'print', 'sync', 'create'] },
            { text: 'Cadastro de NCM', path: '/fiscal/cadastro-de-ncm', actions: ['create', 'edit', 'delete', 'import'] },
            { text: 'Cadastro de CFOP', path: '/fiscal/cadastro-de-cfop', actions: ['create', 'edit', 'delete'] },
            { text: 'Faturamento (Emissão de NF)', path: '/fiscal/emissao-de-nfe', actions: ['create', 'approve']}
        ]
    },
    {
        id: 'controle-de-qualidade',
        text: 'Controle de Qualidade', type: 'group',
        subModules: [
            { text: 'Análise de Embalagens', path: '/controle-de-qualidade/analise-de-embalagens' }
        ]
    },
    {
        id: 'estoque',
        text: 'Estoque', type: 'group',
        subModules: [
            { text: 'Localizações', path: '/estoque/embalagens/localizacoes', actions: ['create', 'edit', 'delete'] },
            { text: 'Entrada de Estoque', path: '/estoque/embalagens/entrada-de-estoque', actions: ['create', 'edit', 'delete', 'import'] },
            { text: 'Saída de Estoque', path: '/estoque/embalagens/saida-de-estoque', actions: ['create', 'edit', 'delete'] },
            { text: 'Consulta de Estoque', path: '/estoque/embalagens/consulta-de-estoque', actions: ['view', 'print', 'move', 'verify'] },
        ]
    },
    {
        id: 'relatorios',
        text: 'Relatórios', path: '/relatorios', type: 'group',
        subModules: [
            { text: 'Estoque Atual', path: '/relatorios/estoque-atual', actions: ['generate', 'delete'] },
            { text: 'Relatório de Usuários', path: '/relatorios/usuarios', actions: ['generate', 'delete'] },
            { text: 'Listagem de Clientes', path: '/relatorios/clientes', actions: ['generate', 'delete'] },
            { text: 'Listagem de Fornecedores', path: '/relatorios/fornecedores', actions: ['generate', 'delete'] },
            { text: 'Listagem de Transportadoras', path: '/relatorios/transportadoras', actions: ['generate', 'delete'] },
            { text: 'Listagem de Produtos', path: '/relatorios/produtos', actions: ['generate', 'delete'] },
            { text: 'Ficha de Produto', path: '/relatorios/ficha-de-produto', actions: ['generate', 'delete'] },
            { text: 'Listagem de Amostras', path: '/relatorios/amostras', actions: ['create', 'generate', 'delete'] },
            { text: 'Relatório de Suporte', path: 'relatorios/suporte', actions: ['generate', 'delete'] },
            { text: 'Materiais', path: '/relatorios/materiais', actions: ['generate', 'delete'] },
        ]
    },
    {
        id: 'helpdesk',
        text: 'AjudaAí!', path: '/helpdesk', type: 'group',
        subModules: [
            { text: 'Central de Suporte', path: '/helpdesk/central-de-suporte', actions: ['create', 'edit', 'delete'] },
            { id: 'tickets_manager', text: 'Gerenciamento de Tickets', path: '/helpdesk/gerenciamento-de-tickets' },
        ]
    },
    {
        id: 'usuarios',
        text: 'Usuários',
        path: '/usuarios',
        type: 'link',
        actions: ['create', 'edit', 'delete']
    },
    {
        id: 'permissoes',
        text: 'Permissões',
        path: '/permissoes'
    },
];

// ============================================
// TUV ERP MENU
// ============================================
const menuItemsTUV = [
    { id: 'tuv_dashboard', text: 'Dashboard', path: '/tuv/dashboard', type: 'link' },
    {
        id: 'tuv_cadastros',
        text: 'Cadastros', type: 'group',
        subModules: [
        ]
    },
    {
        id: 'tuv_models',
        text: 'Modelos', type: 'group',
        subModules: [
            { id: 'tuv_modelo_checklist', text: 'Checklists', path: '/tuv/modelos/checklists', actions: ['create', 'edit', 'delete', 'import', 'export', 'duplicate'] },
        ]
    },
    {
        id: 'tuv_checklist',
        text: 'Checklist', path: '/tuv/checklist', type: 'link', actions: ['create', 'edit', 'delete', 'view']
    },
    {
        id: 'tuv_relatorios',
        text: 'Relatório',
        path: '/tuv/relatorio',
        type: 'link',
        actions: ['view', 'export', 'import', 'delete']
    },
    {
        id: 'helpdesk',
        text: 'AjudaAí!', path: '/helpdesk', type: 'group',
        subModules: [
            { text: 'Central de Suporte', path: '/helpdesk/central-de-suporte', actions: ['create', 'edit', 'delete'] },
            { id: 'tickets_manager', text: 'Gerenciamento de Tickets', path: '/helpdesk/gerenciamento-de-tickets' },
        ]
    },
    { id: 'tuv_permissoes', text: 'Permissões', path: '/tuv/permissoes' },
];

const PermissionsPage = () => {
    const { t } = useTranslation();
    const [selectedRole, setSelectedRole] = useState('');
    const [selectedSystem, setSelectedSystem] = useState(SYSTEM_TYPES.BASE);
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [expandedActions, setExpandedActions] = useState({});

    // Ref to track if we're updating permissions to prevent scroll jumps
    const isUpdatingPermissions = useRef(false);
    const scrollPositionRef = useRef(0);
    const mainContentRef = useRef(null);

    // Detect window size for responsive behavior
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = windowWidth < 768;

    // Get current menu items based on selected system
    const baseMenuItems = useMemo(() =>
        selectedSystem === SYSTEM_TYPES.BASE ? menuItemsBase : menuItemsTUV,
        [selectedSystem]
    );

    // Translate menu items for display
    const currentMenuItems = useMemo(() =>
        baseMenuItems.map(item => translateMenuItem(item, t)),
        [baseMenuItems, t]
    );

    // Get Firestore document ID based on role and system
    const getFirestoreDocId = (role, system) => `${role}_${system}`;

    // Show toast notification
    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
    }, []);

    const handleToastClose = useCallback(() => {
        setToast(null);
    }, []);

    useEffect(() => {
        if (!selectedRole) return;

        const fetchPerms = async () => {
            setLoading(true);
            try {
                const docId = getFirestoreDocId(selectedRole, selectedSystem);
                const docRef = doc(db, 'module_permissions', docId);
                const docSnap = await getDoc(docRef);
                setPermissions(docSnap.exists() ? docSnap.data() : {});
            } catch (error) {
                console.error("Erro ao carregar permissões:", error);
                setPermissions({});
                showToast(t('pages.pages.permissions.error.load'), 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchPerms();
    }, [selectedRole, selectedSystem, showToast, t]);

    const toggleGroup = useCallback((id) => {
        setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const toggleActions = useCallback((id) => {
        setExpandedActions(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    // Auto-expand all groups when searching
    useEffect(() => {
        if (searchTerm) {
            const allGroupIds = [];
            const collectIds = (items) => {
                items.forEach(item => {
                    if (item.subModules && item.subModules.length > 0) {
                        allGroupIds.push(getItemId(item));
                        collectIds(item.subModules);
                    }
                });
            };
            collectIds(currentMenuItems);
            const expanded = {};
            allGroupIds.forEach(id => { expanded[id] = true; });
            setExpandedGroups(expanded);
        }
    }, [searchTerm, currentMenuItems]);

    const handleSave = async () => {
        if (!selectedRole) return;

        setSaveLoading(true);
        try {
            const docId = getFirestoreDocId(selectedRole, selectedSystem);

            // Remove any empty keys from permissions before saving
            const cleanPermissions = {};
            Object.keys(permissions).forEach(key => {
                if (key && key.trim() !== '') {
                    cleanPermissions[key] = permissions[key];
                }
            });

            await setDoc(doc(db, 'module_permissions', docId), cleanPermissions);

            const systemName = selectedSystem === SYSTEM_TYPES.BASE ? 'E-MRP' : 'TUV';
            const roleLabel = ERP_ROLES.find(r => r.value === selectedRole)?.label || selectedRole;
            showToast(
                t('pages.permissions.save.success', { system: systemName, role: roleLabel }),
                'success'
            );
        } catch (e) {
            showToast(t('pages.permissions.save.error'), 'error');
            console.error(e);
        } finally {
            setSaveLoading(false);
        }
    };

    const handleSelectRole = (role) => {
        setSelectedRole(role);
        setExpandedGroups({});
        setExpandedActions({});
        setSearchTerm('');
        if (isMobile) {
            setIsMobileMenuOpen(false);
        }
    };

    const handleSystemChange = (system) => {
        setSelectedSystem(system);
        setExpandedGroups({});
        setExpandedActions({});
        setSearchTerm('');
        if (isMobile) {
            setIsMobileMenuOpen(false);
        }
    };

    const handleSearchChange = (term) => {
        setSearchTerm(term);
    };

    // Function to update permissions without causing scroll jumps
    const updatePermissions = useCallback((newPermissions) => {
        if (mainContentRef.current) {
            scrollPositionRef.current = mainContentRef.current.scrollTop ||
                window.scrollY ||
                document.documentElement.scrollTop;
        }

        isUpdatingPermissions.current = true;
        setPermissions(newPermissions);

        requestAnimationFrame(() => {
            if (mainContentRef.current) {
                mainContentRef.current.scrollTop = scrollPositionRef.current;
            }
            window.scrollTo(0, scrollPositionRef.current);
            isUpdatingPermissions.current = false;
        });
    }, []);

    // Recursive component for menu items with granular actions
    const ModuleRow = useCallback(({ item, level = 0, parentVisible = true }) => {
        const id = getItemId(item);
        const hasChildren = item.subModules && item.subModules.length > 0;
        const isExpanded = expandedGroups[id] || searchTerm.length > 0;
        const areActionsExpanded = expandedActions[id];
        const hasActions = item.actions && item.actions.length > 0;
        const isModuleEnabled = permissions[id] || false;

        // Hide if parent is collapsed (and we aren't searching)
        if (!parentVisible && searchTerm.length === 0) return null;

        // If searching, hide if item and children don't match
        if (searchTerm && !item.text.toLowerCase().includes(searchTerm.toLowerCase())) {
            const hasMatchingChild = item.subModules?.some(sub =>
                sub.text.toLowerCase().includes(searchTerm.toLowerCase())
            );
            if (!hasMatchingChild) return null;
        }

        const levelPadding = isMobile ? Math.min(level * 12, 36) : level * 20;

        // Handle checkbox change
        const handleCheckboxChange = (e) => {
            e.preventDefault();
            e.stopPropagation();

            const checked = e.target.checked;
            const updated = { ...permissions, [id]: checked };

            if (!checked && item.actions) {
                item.actions.forEach(act => {
                    updated[`${id}_${act}`] = false;
                });
                setExpandedActions(prev => ({ ...prev, [id]: false }));
            }

            // Remove any empty keys
            Object.keys(updated).forEach(key => {
                if (!key || key.trim() === '') {
                    delete updated[key];
                }
            });

            updatePermissions(updated);
        };

        // Handle action checkbox change
        const handleActionChange = (actionKey, checked) => {
            const updated = {
                ...permissions,
                [actionKey]: checked
            };

            // Remove any empty keys
            Object.keys(updated).forEach(key => {
                if (!key || key.trim() === '') {
                    delete updated[key];
                }
            });

            updatePermissions(updated);
        };

        // Handle row click
        const handleRowClick = (e) => {
            if (e.target.type === 'checkbox' || e.target.closest('pages.button')) {
                return;
            }
            if (hasChildren) {
                toggleGroup(id);
            }
        };

        // Handle action button click
        const handleActionsToggle = (e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleActions(id);
        };

        // Handle checkbox mouse down to prevent focus
        const handleCheckboxMouseDown = (e) => {
            e.preventDefault();
        };

        // Get translated action label
        const getActionLabel = (actionKey) => {
            const translationKey = ACTION_LABELS[actionKey] || `actions.${actionKey}`;
            return t(translationKey);
        };

        return (
            <React.Fragment key={id}>
                <div
                    className={`${permissionsStyles.moduleRow} ${hasChildren ? permissionsStyles.hasChildren : ''} ${isModuleEnabled ? permissionsStyles.enabled : ''}`}
                    style={{ paddingLeft: `${levelPadding + 20}px` }}
                    onClick={handleRowClick}
                >
                    <div className={permissionsStyles.moduleInfo}>
                        {hasChildren && (
                            <span className={`${permissionsStyles.expandIcon} ${isExpanded ? permissionsStyles.expanded : ''}`}>
                                ▶
                            </span>
                        )}
                        <span className={`${permissionsStyles.moduleText} ${level === 0 ? permissionsStyles.level0 : permissionsStyles.levelN}`}>
                            {item.text}
                        </span>
                        {hasActions && (
                            <span className={permissionsStyles.actionCount}>
                                {item.actions.length}
                            </span>
                        )}
                    </div>

                    <div className={permissionsStyles.moduleControls}>
                        {hasActions && isModuleEnabled && (
                            <button
                                className={`${permissionsStyles.expandActionsBtn} ${areActionsExpanded ? permissionsStyles.active : ''}`}
                                onClick={handleActionsToggle}
                                onMouseDown={(e) => e.preventDefault()}
                                title={areActionsExpanded ? t('pages.permissions.actions.hide') : t('pages.permissions.actions.view')}
                                type="button"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        )}
                        <div className={permissionsStyles.checkboxWrapper}>
                            <input
                                type="checkbox"
                                className={permissionsStyles.checkbox}
                                checked={isModuleEnabled}
                                onChange={handleCheckboxChange}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onMouseDown={handleCheckboxMouseDown}
                            />
                        </div>
                    </div>
                </div>

                {/* Granular Actions - Collapsible */}
                {hasActions && isModuleEnabled && areActionsExpanded && (
                    <div
                        className={permissionsStyles.actionsContainer}
                        style={{ paddingLeft: `${Math.min(levelPadding + 44, 80)}px` }}
                    >
                        <div className={permissionsStyles.actionsGrid}>
                            {item.actions.map(action => {
                                const actionKey = `${id}_${action}`;
                                const label = getActionLabel(action);
                                return (
                                    <label key={actionKey} className={permissionsStyles.actionChip}>
                                        <input
                                            type="checkbox"
                                            className={permissionsStyles.actionCheckbox}
                                            checked={permissions[actionKey] || false}
                                            onChange={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleActionChange(actionKey, e.target.checked);
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onMouseDown={(e) => e.preventDefault()}
                                        />
                                        <span className={permissionsStyles.actionLabel}>{label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Children modules */}
                {hasChildren && isExpanded && item.subModules.map(sub => (
                    <ModuleRow
                        key={getItemId(sub)}
                        item={sub}
                        level={level + 1}
                        parentVisible={isExpanded}
                    />
                ))}
            </React.Fragment>
        );
    }, [expandedGroups, expandedActions, searchTerm, permissions, isMobile, toggleGroup, toggleActions, updatePermissions, t]);

    // Removed unused getPermissionStats function and stats variable

    return (
        <div className={permissionsStyles.pageContainer}>
            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={handleToastClose}
                />
            )}

            <div className={permissionsStyles.header}>
                <div className={permissionsStyles.headerInfo}>
                    <h1 className={permissionsStyles.headerTitle}>{t('pages.permissions.title')}</h1>
                    <p className={permissionsStyles.headerSubtitle}>
                        {t('pages.permissions.subtitle')}
                    </p>
                </div>
                {selectedRole && (
                    <div className={permissionsStyles.headerActions}>
                        <button
                            className={`${styles.addButton} ${permissionsStyles.saveButton}`}
                            onClick={handleSave}
                            disabled={saveLoading}
                        >
                            {saveLoading ? (
                                <>
                                    <span className={permissionsStyles.spinnerSmall}></span>
                                    {t('pages.permissions.saving')}
                                </>
                            ) : (
                                t('pages.permissions.saveButton')
                            )}
                        </button>
                    </div>
                )}
            </div>

            <div className={`${permissionsStyles.twoColumnLayout} ${isMobile ? permissionsStyles.mobileLayout : ''}`}>
                {/* Sidebar */}
                <div className={`${permissionsStyles.sidebar} ${isMobile && !isMobileMenuOpen ? permissionsStyles.sidebarCollapsed : ''}`}>
                    <div className={permissionsStyles.sidebarCard}>
                        {/* System Toggle */}
                        <div className={permissionsStyles.formGroup}>
                            <label className={permissionsStyles.label}>{t('pages.permissions.system')}</label>
                            <div className={permissionsStyles.systemToggle}>
                                <button
                                    className={`${permissionsStyles.systemBtn} ${selectedSystem === SYSTEM_TYPES.BASE ? permissionsStyles.activeSystem : ''}`}
                                    onClick={() => handleSystemChange(SYSTEM_TYPES.BASE)}
                                >
                                    E-MRP
                                </button>
                                <button
                                    className={`${permissionsStyles.systemBtn} ${selectedSystem === SYSTEM_TYPES.LUXURY ? permissionsStyles.activeSystem : ''}`}
                                    onClick={() => handleSystemChange(SYSTEM_TYPES.LUXURY)}
                                >
                                    TUV
                                </button>
                            </div>
                        </div>

                        {/* Role Selection */}
                        <div className={permissionsStyles.formGroup}>
                            <label className={permissionsStyles.label}>{t('pages.permissions.role')}</label>
                            <select
                                className={permissionsStyles.select}
                                value={selectedRole}
                                onChange={(e) => handleSelectRole(e.target.value)}
                            >
                                <option value="">{t('pages.permissions.selectRole')}</option>
                                {ERP_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                        </div>

                        {/* Search Input */}
                        {selectedRole && (
                            <div className={permissionsStyles.formGroup}>
                                <label className={permissionsStyles.label}>{t('pages.permissions.searchModule')}</label>
                                <SearchBar
                                    placeholder={t('pages.permissions.searchPlaceholder')}
                                    onSearch={handleSearchChange}
                                    initialValue={searchTerm}
                                    debounceDelay={200}
                                />
                            </div>
                        )}

                        {/* Mobile Toggle Button */}
                        {isMobile && selectedRole && (
                            <button
                                className={permissionsStyles.mobileToggleBtn}
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            >
                                {isMobileMenuOpen ? t('pages.permissions.closeModules') : t('pages.permissions.openModules')}
                            </button>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div
                    className={permissionsStyles.mainContent}
                    ref={mainContentRef}
                >
                    {!selectedRole ? (
                        <EmptyState
                            title={t('pages.permissions.emptyState.title')}
                            message={t('pages.permissions.emptyState.message')}
                        />
                    ) : loading ? (
                        <div className={permissionsStyles.loadingState}>
                            <div className={permissionsStyles.spinner}></div>
                            <p>{t('pages.permissions.loading')}</p>
                        </div>
                    ) : (
                        <div className={permissionsStyles.permissionsList}>
                            <div className={permissionsStyles.listHeader}>
                                <span>{t('pages.permissions.table.module')}</span>
                                <span>{t('pages.permissions.table.permission')}</span>
                            </div>
                            {currentMenuItems.map(item => (
                                <ModuleRow key={getItemId(item)} item={item} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Floating Save Button for Mobile */}
            {selectedRole && isMobile && (
                <button
                    className={permissionsStyles.floatingSaveBtn}
                    onClick={handleSave}
                    disabled={saveLoading}
                >
                    {saveLoading ? '...' : '💾'}
                </button>
            )}
        </div>
    );
};

export default PermissionsPage;